import { Box, Button, Checkbox, FormControlLabel, Paper, TextField, Typography } from '@mui/material';
import { red } from '@mui/material/colors';
import { Field, Form, Formik } from 'formik';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ContactSelector } from '@sophocles/components/contacts';
import FormErrorAlert from '@sophocles/components/FormErrorAlert';
import IssueCategorySelectComponent from '@sophocles/components/IssueCategorySelectComponent';

const DEFAULT_ISSUE_CATEGORY = 4;
const DEFAULT_ISSUE_SUBCATEGORY = 2;

const INITIAL_VALUES = {
  reported_by: '',
  problem_description: '',
  resolution: '',
  internal_notes: '',
};

// Filled multiline inputs vertically center short text in a tall
// (minRows={3}) slot by default; anchor typed text to the top instead.
const multilineFieldSx = {
  '& .MuiInputBase-root.MuiInputBase-multiline': {
    alignItems: 'flex-start',
  },
  '& .MuiInputBase-inputMultiline': {
    alignSelf: 'flex-start',
    padding: '0 !important',
  },
};

export default function NewTicketForm({
  customer,
  selectedPlant,
  selectedStructure,
  onSubmit,
  onCancel,
  errorMessages,
}) {
  const { t } = useTranslation();

  const [issueCategory, setIssueCategory] = useState(DEFAULT_ISSUE_CATEGORY);
  const [issueSubCategory, setIssueSubCategory] = useState(DEFAULT_ISSUE_SUBCATEGORY);
  const [eligibleForRma, setEligibleForRma] = useState(true);
  const [createRMA, setCreateRMA] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);

  const handleIssueCategoryChange = (category, subCategory, isEligibleForRma) => {
    setIssueCategory(category);
    setIssueSubCategory(subCategory);
    setEligibleForRma(isEligibleForRma);
  };

  const handleSubmit = values => {
    onSubmit(
      {
        ...values,
        issueCategory_id: issueCategory,
        issue_id: issueSubCategory,
      },
      { createRMA, selectedContact },
    );
  };

  const radio = selectedStructure ? selectedStructure.getIn(['relay_controller', 'radio'], null) : null;
  const customerId = customer?.get('id');
  const rmaBlocked = createRMA && !eligibleForRma;

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', overflow: 'auto' }}>
      <Box sx={{ flex: '0 1 700px', width: '100%' }}>
        <Paper sx={{ p: 2, mb: 2 }}>
          <Formik initialValues={INITIAL_VALUES} onSubmit={handleSubmit}>
            {({ submitForm }) => (
              <Form>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  {t('tickets.ticketDetails', 'Ticket Details')}
                </Typography>
                <Box component="ul" sx={{ listStyle: 'none', p: 0, mb: 2 }}>
                  <li>
                    {t('general.customer', 'Customer')}:{' '}
                    <span>
                      {customer.get('id', 0)}. {customer.get('name', 'none')}
                    </span>
                  </li>
                  {selectedPlant ? (
                    <li>
                      {t('general.plant', 'Plant')}: {selectedPlant.name ?? '0'}. {selectedPlant.description ?? 'none'}
                    </li>
                  ) : null}
                  {selectedStructure ? (
                    <li>
                      {t('general.site', 'Site')}: {selectedStructure.get('name', '0')}
                    </li>
                  ) : null}
                  {radio ? <li>ICCID: {radio.get('iccid', '')}</li> : null}
                </Box>

                <Field name="reported_by">
                  {({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      margin="normal"
                      label={t('tickets.nameOfCaller', 'Name of Caller')}
                    />
                  )}
                </Field>

                {customerId && (
                  <Box sx={{ my: 2 }}>
                    <ContactSelector
                      label={t('tickets.contactOptional', 'Contact (Optional)')}
                      value={selectedContact}
                      onChange={contact => setSelectedContact(contact || null)}
                      customerId={customerId}
                      siteGroupId={selectedPlant?.id}
                      customer={customer}
                      siteGroup={selectedPlant}
                      showAddress={false}
                      required={false}
                      quickMode
                      requireCompleteAddress={false}
                      showDefaultAddresses={false}
                      primaryContext="none"
                      requiredFields={['name', 'email']}
                    />
                  </Box>
                )}

                <Field name="problem_description">
                  {({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      multiline
                      minRows={3}
                      margin="normal"
                      label={t('tickets.problemDescription', 'Problem Description')}
                      sx={multilineFieldSx}
                    />
                  )}
                </Field>

                <Box sx={{ mt: 2 }}>
                  <IssueCategorySelectComponent
                    category={issueCategory}
                    subCategory={issueSubCategory}
                    onChange={handleIssueCategoryChange}
                  />
                </Box>
                {rmaBlocked && (
                  <Typography component="span" sx={{ color: red[500] }}>
                    {t('tickets.categoryNotValidForRma', 'Selected category is not eligible for RMA')}
                  </Typography>
                )}

                <Box sx={{ width: 300, mt: 1 }}>
                  <FormControlLabel
                    label={t('tickets.createRmaFromTicket', 'Create RMA from this Ticket')}
                    control={<Checkbox checked={createRMA} onChange={() => setCreateRMA(prev => !prev)} />}
                  />
                </Box>

                <Field name="resolution">
                  {({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      multiline
                      minRows={3}
                      margin="normal"
                      label={t('tickets.resolution', 'Resolution')}
                      sx={multilineFieldSx}
                    />
                  )}
                </Field>

                <Field name="internal_notes">
                  {({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      multiline
                      minRows={3}
                      margin="normal"
                      label={t('tickets.internalComments', 'Internal Comments')}
                      sx={multilineFieldSx}
                    />
                  )}
                </Field>

                <FormErrorAlert messages={errorMessages} sx={{ mt: 2 }} />

                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row-reverse',
                    py: 2,
                    justifyContent: 'space-between',
                  }}
                >
                  <Button variant="contained" color="primary" disabled={rmaBlocked} onClick={submitForm}>
                    {t('actions.submit', 'Submit')}
                  </Button>
                  <Button variant="contained" color="secondary" onClick={onCancel}>
                    {t('actions.cancel', 'Cancel')}
                  </Button>
                </Box>
              </Form>
            )}
          </Formik>
        </Paper>
      </Box>
    </Box>
  );
}
